import { zodResolver } from "@hookform/resolvers/zod";
import { Stack, SxProps, Theme } from "@mui/material";
import { memo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import Button from "../../../../components/UI/Button";
import DateInput from "../../../../components/UI/Inputs/DateInput";
import FormInput from "../../../../components/UI/Inputs/FormInput";
import Loading from "../../../../components/UI/Loading";
import { TAddHealth, addInsuranceSchema } from "./schema";
import SelectFamilyMember from "./SelectFamilyMember";
import SelectInsurance from "./SelectInsurance";
import { useInsurance } from "./useInsurance";
import FormCheckBox from "../../../../components/UI/Inputs/FormCheckBox";

const KEYS = addInsuranceSchema.keyof().Enum;

interface IInsuranceFormProps {
  onSuccess: () => void;
  sx?: SxProps<Theme>;
}

const InsuranceForm: React.FC<IInsuranceFormProps> = ({ onSuccess, sx }) => {
  const { t } = useTranslation();

  const methods = useForm<TAddHealth>({
    resolver: zodResolver(addInsuranceSchema),
  });

  const { isEditMode, loading, getLoading, onSubmit } = useInsurance(
    methods,
    onSuccess
  );

  return (
    <FormProvider {...methods}>
      <Stack gap={2} sx={{ mt: 2, ...sx }}>
        {getLoading && <Loading />}
        <SelectInsurance />
        <SelectFamilyMember disable={isEditMode} />
        <DateInput
          hasDefaultDate={false}
          label={t("page.schedules.insurance.addInsurance.validity.label")}
          name={KEYS.validate}
        />
        <FormInput
          size="small"
          fullWidth
          label={t("page.schedules.insurance.addInsurance.number.label")}
          name={KEYS.code}
        />
        <FormCheckBox
          name="main"
          label={`${t("page.schedules.insurance.addInsurance.main")}`}
        />
        <Button
          variant="contained"
          sx={{ alignSelf: "end" }}
          onClick={methods.handleSubmit(onSubmit)}
          loading={loading}
        >
          {isEditMode
            ? t("page.schedules.insurance.addInsurance.buttonEdit")
            : t("page.schedules.insurance.addInsurance.buttonSave")}
        </Button>
      </Stack>
    </FormProvider>
  );
};

export default memo(InsuranceForm);
